import { create, type StoreApi, type UseBoundStore } from "zustand";
import {
  createSeenTracker,
  matchesFilter,
  pinnedRoundId,
  selectNextRound,
  sourceFromRounds,
  type RoundFilter,
  type RoundSource,
} from "../lib/rounds";
import type { RoundIndexEntry } from "../lib/roundIndex";
import { storage as appStorage, type TypedStorage } from "../lib/storage";
import { playCorrect, playWrong } from "../lib/sound";
import {
  trackRoundCompleted,
  type AnalyticsHorizon,
  type AnalyticsMode,
  type AnalyticsTimeframe,
} from "../lib/analytics";
import { useHistoryStore, type HistoryEntryInput } from "./historyStore";
import type { Preset } from "../types";

/**
 * The Round-lifecycle factory behind every Loop (see CONTEXT.md "Loop").
 * Predict daily, Predict weekly, and Trade are instances of this one module:
 * the lifecycle owns load, pick with seen-tracking, the fallback warning,
 * commit, reveal, next, and (optionally) persisted hit-rate scoring. A Loop's
 * config supplies only its bank, its filter, and how a commitment resolves.
 */

export type Phase = "analyzing" | "revealed";
export type Status = "loading" | "ready" | "error";
export type Score = { played: number; correct: number };
export type Streak = { current: number; best: number };

export type FetchLike = (url: string) => Promise<{ json: () => Promise<unknown> }>;

/** What a Round must carry to be pickable by the lifecycle. */
type Playable = { id: string; instrumentId: string; context: unknown[] };

/** An instrument the loaded bank actually carries, for the markets list. */
export type LoopMarket = { id: string; name: string };

export interface LoopState<R, C, V> {
  status: Status;
  /**
   * Every Round in the bank, without its candles (GRA-152). Selection reads
   * this; the UI reads it only for what a Round *is*, never for what it looks
   * like. It replaced a `rounds: R[]` holding the whole decoded bank, which is
   * exactly the tens of megabytes this Loop no longer loads.
   */
  index: RoundIndexEntry[];
  /** The instruments the loaded bank carries (the markets list). */
  instruments: LoopMarket[];
  current: R | null;
  phase: Phase;
  /** A notice when settings emptied the pool and selection fell back (spec §13). */
  warning: string | null;
  /** The player's commitment to the current Round (a guess or a trade). */
  commit: C | null;
  /** The Loop's reveal state (resolution bundle, trade result), set at commit. */
  reveal: V | null;
  /**
   * The player's own one-line reason (GRA-135), typed before committing and
   * still readable at reveal. Cleared on `next`/`hydrate`, same as `commit`
   * and `reveal`; unlike those two it survives `submit` itself, since the
   * reveal banner shows it back next to the outcome.
   */
  note: string;
  /** Update the in-progress reason note; the empty string is "no note". */
  setNote: (note: string) => void;
  /**
   * Finite Loops only (`run`, GRA-139): how many Rounds of the run are
   * complete, and whether the run has ended. An endless Loop stays at 0 and
   * false forever, so a caller can read them without asking which kind it has.
   */
  runProgress: number;
  runOver: boolean;

  /** Open the bank's index and start the first Round. */
  init: (baseUrl?: string, fetchFn?: FetchLike) => Promise<void>;
  /** Start playing from an already-loaded set of Rounds (used by tests). */
  hydrate: (rounds: R[]) => void;
  /**
   * Start over from the current source: re-pick (endless Loop) or re-derive
   * the run order (finite Loop). What `hydrate` used to be asked to do by
   * handing it back the Rounds it already held, now that the Loop no longer
   * holds them.
   */
  restart: () => void;
  /** Commit to the current Round: resolve via the Loop's adapter and reveal. */
  submit: (commit: C) => void;
  /** Advance to the next unseen Round (reshuffling when exhausted). */
  next: () => void;
  /**
   * Re-check the on-screen Round against the filter right now (GRA-140): a
   * setting a Loop's filter reads (e.g. difficulty preset) can change mid-Round
   * and invalidate a Round that was eligible when it was picked. A no-op once
   * the player has committed (the reveal already happened under the old
   * filter) or before a bank has loaded; still eligible Rounds are left alone
   * rather than swapped for no reason.
   */
  revalidate: () => void;
}

/** A Loop with hit-rate scoring: persisted Score + Streak. */
export interface ScoredLoopState<R, C, V> extends LoopState<R, C, V> {
  score: Score;
  streak: Streak;
}

export type LoopScoring = { scoreKey: string; streakKey: string };

/**
 * A finite Loop (the daily challenge, GRA-139): a fixed ordered run instead of
 * endless random picks. The lifecycle owns only the sequencing (which Round
 * is on screen, how far in the run we are, when it ends) and hands every
 * decision about what the run *means* (which Rounds, where to resume, whether
 * this attempt counts) back to the Loop's own module through these callbacks.
 */
export type LoopRun = {
  /**
   * The run's Round ids in play order, derived from the *index* at hydrate
   * (GRA-152). Ids rather than Rounds: deciding the run is what the index is
   * for, and the ten Rounds themselves are fetched afterwards, so a challenge
   * costs ten shards instead of a bank.
   */
  order: (index: RoundIndexEntry[]) => string[];
  /**
   * How many Rounds of the run are already complete, read at hydrate. This is
   * what makes a run survive a reload: the Loop resumes at the first Round the
   * player has not committed to. Values past the end open a finished run.
   */
  resumeAt: () => number;
  /** One completed Round of the run: its 0-based index and whether it was right. */
  record: (index: number, correct: boolean) => void;
};

export type RoundLoopConfig<R extends Playable, C, V> = {
  /** Open this Loop's Round source: its index now, its shards on demand. */
  openSource: (baseUrl: string, fetchFn: FetchLike) => Promise<RoundSource<R>>;
  /** Storage key for this Loop's seen-Round ids. */
  seenKey: string;
  /**
   * The eligible-Round filter, read fresh at every pick. A Loop that draws
   * from its whole bank states that with `() => ({})`.
   */
  filter: () => RoundFilter;
  /**
   * The Loop's adapter: how a commitment resolves against the current Round.
   * Side effects that belong to the commit (applying P&L) happen here.
   * `correct` drives scoring and is required when `scoring` is set.
   */
  resolve: (round: R, commit: C) => { reveal: V; correct?: boolean };
  /** Persisted hit-rate scoring; omit for a Loop that scores elsewhere (Trade). */
  scoring?: LoopScoring;
  /**
   * Build this Loop's history entry from a completed Round (GRA-123). Omit
   * for a Loop that should not be logged (none today). `correct` mirrors
   * `resolve`'s own return: undefined for a Loop with no notion of it (Trade).
   * `note` is the player's own reason text (GRA-135), already trimmed and
   * `undefined` when blank.
   */
  history?: (
    round: R,
    commit: C,
    reveal: V,
    correct: boolean | undefined,
    note: string | undefined,
  ) => HistoryEntryInput;
  /**
   * Play a fixed finite run rather than endless random picks (GRA-139). Set,
   * `filter` is never consulted: the run's `order` is the whole selection.
   */
  run?: LoopRun;
  /** Storage dependency, injectable by tests. Defaults to the app storage. */
  storage?: TypedStorage;
  /**
   * The `round_completed` beacon (GRA-100). Omit for a Loop that should not
   * report at all (none today). Reports the shape of the Round only, never
   * its id, instrument, or date; `correct` defaults to `resolve`'s own
   * `correct` and is overridable for a Loop with no such notion (Trade,
   * which reports its win/loss instead).
   */
  analytics?: {
    mode: AnalyticsMode;
    /**
     * The timeframe reported. A thunk for a Loop whose timeframe is a live
     * setting rather than fixed by the Loop itself (Trade, ADR-0013).
     */
    timeframe: AnalyticsTimeframe | (() => AnalyticsTimeframe);
    horizon: AnalyticsHorizon;
    /** The active difficulty preset, read fresh at every commit. */
    preset: () => Preset;
    correct?: (round: R, commit: C, reveal: V, correct: boolean | undefined) => boolean;
  };
};

export function createRoundLoop<R extends Playable, C, V>(
  config: RoundLoopConfig<R, C, V> & { scoring: LoopScoring },
): UseBoundStore<StoreApi<ScoredLoopState<R, C, V>>>;
export function createRoundLoop<R extends Playable, C, V>(
  config: RoundLoopConfig<R, C, V> & { scoring?: undefined },
): UseBoundStore<StoreApi<LoopState<R, C, V>>>;
export function createRoundLoop<R extends Playable, C, V>(
  config: RoundLoopConfig<R, C, V>,
): UseBoundStore<StoreApi<ScoredLoopState<R, C, V>>> {
  const { scoring, run } = config;
  const store = config.storage ?? appStorage;
  const seen = createSeenTracker(store, config.seenKey);

  // The bank the Loop is playing: its index, plus whichever shards have been
  // fetched. Held outside the store because it is machinery, not view state.
  let source: RoundSource<R> | null = null;

  // A finite Loop's run, resolved once per hydrate rather than per pick: the
  // order is a pure derivation from the index, and recomputing it on every
  // `next` would hash the whole index again for no gain.
  let runOrder: string[] = [];

  /**
   * The Round chosen next, decided at the moment the current one is committed
   * rather than at `next`. Choosing early is what pays for on-demand shards:
   * the fetch runs during the reveal, so by the time the player presses Enter
   * the Round is already parsed and `next` is as instant as it ever was. Any
   * event that could invalidate the choice (a filter change, a restart)
   * clears it, and `next` then chooses afresh.
   */
  let chosenNext: Chosen | null = null;

  /**
   * Guards against a stale fetch winning. Two picks can be in flight when the
   * player changes a setting mid-load; only the newest may write `current`.
   */
  let pickToken = 0;

  type Chosen = { id: string; warning: string | null };

  /**
   * Which Round to play next, decided from the index alone. No fetch, no
   * promise: this is the whole of selection, and it is deliberately the same
   * `selectNextRound` the fully-loaded bank used, over entries that satisfy
   * the same filter contract.
   */
  const choose = (): Chosen | null => {
    if (!source) return null;
    // Dev-only override: a pinned `?round=<id>` always wins, with no
    // seen-tracking side effects, so browser automation can reproduce the
    // same chart across reloads. Inert in production builds.
    const pinnedId = pinnedRoundId();
    if (pinnedId && source.index.some((entry) => entry.id === pinnedId)) {
      return { id: pinnedId, warning: null };
    }

    const selection = selectNextRound(source.index, seen.ids(), config.filter());
    if (!selection) return null;
    if (selection.reshuffled) seen.reset();
    return {
      id: selection.round.id,
      // Surface the fallback warning only when the filter actually fell back.
      warning: selection.fellBack ? (selection.warning ?? null) : null,
    };
  };

  const blank = { phase: "analyzing" as Phase, commit: null, reveal: null, note: "" };

  return create<ScoredLoopState<R, C, V>>((set, get) => {
    /**
     * Put `chosen` on screen. Synchronous when its shard is already parsed
     * (the ordinary case, thanks to `chosenNext`), and otherwise a short
     * "loading" while the one shard it needs comes down. Deliberately not
     * `await`ed by its callers: `hydrate`, `next` and `revalidate` stay the
     * synchronous store actions the views call.
     */
    const settle = (chosen: Chosen | null) => {
      const token = (pickToken += 1);
      if (!chosen || !source) {
        set({ status: "ready", current: null, warning: null, ...blank });
        return;
      }
      const cached = source.peek(chosen.id);
      if (cached) {
        set({ status: "ready", current: cached, warning: chosen.warning, ...blank });
        return;
      }
      set({ status: "loading" });
      void source
        .get(chosen.id)
        .then((round) => {
          if (token !== pickToken) return;
          set({
            status: round ? "ready" : "error",
            current: round,
            warning: chosen.warning,
            ...blank,
          });
        })
        .catch(() => {
          if (token === pickToken) set({ status: "error" });
        });
    };

    /** Open the run (finite Loop) or pick the first Round (endless Loop). */
    const start = () => {
      if (!source) return;
      set({ index: source.index, instruments: source.instruments });
      chosenNext = null;
      if (run) {
        runOrder = run.order(source.index);
        // Clamp rather than trust: stored progress can outlive the run it was
        // written for (a shorter bank, a hand-edited value), and a run that
        // opens past its own end is simply a finished one.
        const progress = Math.min(Math.max(run.resumeAt(), 0), runOrder.length);
        set({ runProgress: progress, runOver: progress >= runOrder.length });
        // Every Round of the run, warmed at once: ten shards is the whole cost
        // of a challenge, and fetching them together means only the first
        // Round ever waits.
        void source.getMany(runOrder).catch(() => {
          // The per-Round `settle` below reports a failure for real.
        });
        const id = runOrder[progress];
        settle(id ? { id, warning: null } : null);
        return;
      }
      settle(choose());
    };

    return {
    status: "loading",
    index: [],
    instruments: [],
    current: null,
    phase: "analyzing",
    warning: null,
    commit: null,
    reveal: null,
    note: "",
    setNote: (note) => set({ note }),
    runProgress: 0,
    runOver: false,
    score: scoring
      ? store.get<Score>(scoring.scoreKey, { played: 0, correct: 0 })
      : { played: 0, correct: 0 },
    streak: scoring
      ? store.get<Streak>(scoring.streakKey, { current: 0, best: 0 })
      : { current: 0, best: 0 },

    init: async (baseUrl = "/data", fetchFn = fetch) => {
      set({ status: "loading" });
      try {
        const opened = await config.openSource(baseUrl, fetchFn);
        if (opened.index.length === 0) {
          set({ status: "error" });
          return;
        }
        source = opened;
        start();
      } catch {
        set({ status: "error" });
      }
    },

    hydrate: (rounds) => {
      source = sourceFromRounds(rounds);
      start();
    },

    restart: () => {
      start();
    },

    submit: (commit) => {
      const { current, phase, note } = get();
      if (!current || phase !== "analyzing") return;

      const { reveal, correct } = config.resolve(current, commit);
      if (correct === true) playCorrect();
      else if (correct === false) playWrong();
      seen.add(current.id);

      // One `round_completed` per commit, whatever kind of Loop this is: the
      // guard above (`phase !== "analyzing"` returns early) is what keeps a
      // held Enter from double-firing this the same way it keeps `next` from
      // double-advancing, and the daily challenge's finite run goes through
      // this same `submit` for each of its Rounds, so it needs no seam of
      // its own to avoid double-counting.
      if (config.analytics) {
        const a = config.analytics;
        trackRoundCompleted({
          mode: a.mode,
          timeframe: typeof a.timeframe === "function" ? a.timeframe() : a.timeframe,
          horizon: a.horizon,
          preset: a.preset(),
          correct: a.correct ? a.correct(current, commit, reveal, correct) : correct === true,
        });
      }

      const trimmedNote = note.trim() === "" ? undefined : note.trim();
      if (config.history) {
        useHistoryStore
          .getState()
          .appendHistory(config.history(current, commit, reveal, correct, trimmedNote));
      }

      // The run advances at commit, not at `next`: the Round is decided the
      // moment it is revealed, so a reload from the reveal screen must resume
      // at the *following* Round rather than replay one already answered.
      const runPatch = run
        ? { runProgress: get().runProgress + 1 }
        : null;
      if (run) run.record(get().runProgress, correct === true);

      if (scoring) {
        const { score, streak } = get();
        const nextScore: Score = {
          played: score.played + 1,
          correct: score.correct + (correct ? 1 : 0),
        };
        const grown = correct ? streak.current + 1 : 0;
        const nextStreak: Streak = {
          current: grown,
          best: Math.max(streak.best, grown),
        };
        store.set(scoring.scoreKey, nextScore);
        store.set(scoring.streakKey, nextStreak);
        set({
          commit,
          reveal,
          phase: "revealed",
          score: nextScore,
          streak: nextStreak,
          ...runPatch,
        });
      } else {
        set({ commit, reveal, phase: "revealed", ...runPatch });
      }

      // Choose the next Round now and start fetching its shard, so it is
      // parsed by the time the player leaves the reveal. Only for an endless
      // Loop: a run's Rounds were all warmed when it opened.
      if (!run && source) {
        chosenNext = choose();
        if (chosenNext) {
          void source.get(chosenNext.id).catch(() => {
            // `next` re-reports through `settle` if it is still not there.
          });
        }
      }
    },

    next: () => {
      // Guard the revealed→analyzing edge: a held/repeated Enter must not
      // advance twice (and skip a Round) before the Enter listener is torn down.
      if (get().phase !== "revealed") return;

      if (run) {
        const id = runOrder[get().runProgress];
        set({ runOver: id === undefined });
        settle(id ? { id, warning: null } : null);
        return;
      }

      const chosen = chosenNext ?? choose();
      chosenNext = null;
      settle(chosen);
    },

    revalidate: () => {
      // A run's Rounds are fixed by its seed, not by settings, so there is
      // nothing a changed filter could invalidate.
      if (run) return;
      const { current, phase, index } = get();
      if (!current || phase !== "analyzing" || index.length === 0) return;
      if (matchesFilter(current, config.filter())) return;
      // The Round chosen under the old filter is no better than the one on
      // screen, so it goes too rather than being served next.
      chosenNext = null;
      settle(choose());
    },
    };
  });
}

/** Sentinel for "no stored value" (a JSON parse can never return this object). */
const ABSENT = {};

/**
 * One-time storage-key rename (finishing ADR-0006's vocabulary sweep at the
 * storage tier): if `oldKey` holds a value, copy it to `newKey` unless the new
 * key is already populated, then remove the old key. Mirrors the
 * migrateStoredSettings precedent.
 */
export function migrateStorageKey(
  store: TypedStorage,
  oldKey: string,
  newKey: string,
): void {
  const value = store.get<unknown>(oldKey, ABSENT);
  if (value === ABSENT) return;
  if (store.get<unknown>(newKey, ABSENT) === ABSENT) store.set(newKey, value);
  store.remove(oldKey);
}
